import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Calendar, RefreshCw, ChevronLeft, ChevronRight, Banknote, Building,
  CreditCard, TrendingUp, Check, Loader2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/config';

const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) : '-';
const fmtCurrency = (v) => (v || 0).toFixed(2).replace('.', ',');

const PLATFORM_LABELS = {
  booking: 'Booking', airbnb: 'Airbnb', holiday_world: 'HW',
  atraveo: 'Atraveo', interhome: 'Interhome', other: 'Altro'
};
const PLATFORM_COLORS = {
  booking: 'bg-blue-100 text-blue-800',
  airbnb: 'bg-rose-100 text-rose-800',
  holiday_world: 'bg-amber-100 text-amber-800',
  atraveo: 'bg-teal-100 text-teal-800',
  interhome: 'bg-purple-100 text-purple-800',
  other: 'bg-slate-100 text-slate-800'
};

function CollectionTable({ entries, emptyMessage, colorScheme = 'amber', showToCollect = true, onMarkCollected, markingId }) {
  if (!entries || entries.length === 0) {
    return <p className="text-sm text-slate-400 py-4 text-center">{emptyMessage}</p>;
  }
  const borderColor = colorScheme === 'amber' ? 'border-amber-300' : 'border-emerald-300';
  const bgColor = colorScheme === 'amber' ? 'bg-amber-50' : 'bg-emerald-50';
  const textColor = colorScheme === 'amber' ? 'text-amber-800' : 'text-emerald-800';
  const boldColor = colorScheme === 'amber' ? 'text-amber-900' : 'text-emerald-900';
  const highlightColor = colorScheme === 'amber' ? 'text-amber-700' : 'text-emerald-700';

  const pendingEntries = entries.filter(e => !e.incassato_confermato);
  const confirmedEntries = entries.filter(e => e.incassato_confermato);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid={`${colorScheme}-collections-table`}>
        <thead className="bg-slate-50 sticky top-0">
          <tr>
            <th className="px-3 py-2.5 text-left font-semibold border-b border-r border-slate-200">IMMOBILE</th>
            <th className="px-3 py-2.5 text-left font-semibold border-b border-r border-slate-200">N PREN.</th>
            <th className="px-3 py-2.5 text-left font-semibold border-b border-r border-slate-200">OSPITE</th>
            <th className="px-3 py-2.5 text-center font-semibold border-b border-r border-slate-200 w-16">PIATT.</th>
            <th className="px-3 py-2.5 text-center font-semibold border-b border-r border-slate-200 w-14">N/R</th>
            <th className="px-3 py-2.5 text-center font-semibold border-b border-r border-slate-200 w-16">STATO</th>
            <th className="px-3 py-2.5 text-center font-semibold border-b border-r border-slate-200 w-20">CHECK-IN</th>
            <th className="px-3 py-2.5 text-center font-semibold border-b border-r border-slate-200 w-20">CHECK-OUT</th>
            <th className="px-3 py-2.5 text-right font-semibold border-b border-r border-slate-200 w-20">PREZZO</th>
            <th className="px-3 py-2.5 text-right font-semibold border-b border-r border-slate-200 w-20">GIA' PRESI</th>
            <th className={`px-3 py-2.5 text-right font-semibold border-b border-r border-slate-200 w-24 ${highlightColor}`}>
              {showToCollect ? 'DA INCASSARE' : 'IMPORTO'}
            </th>
            {onMarkCollected && <th className="px-3 py-2.5 text-center font-semibold border-b border-slate-200 w-20">INCASSO</th>}
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => {
            const isPenalty = e.is_cancellation_penalty;
            const isConfirmed = e.incassato_confermato;
            const rowBg = isConfirmed ? 'bg-green-50' : isPenalty ? 'bg-red-50/60' : '';
            const rowBorder = isConfirmed ? 'border-green-200' : '';
            return (
              <tr key={e.id || i} className={`border-b ${rowBg} ${rowBorder} ${!isConfirmed ? 'hover:bg-slate-50/50' : ''}`} data-testid={`collection-row-${e.id}`}>
                <td className="px-3 py-2 border-r border-slate-100">
                  <div className="flex items-center gap-1.5">
                    <Building className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                    <span className={`font-medium truncate max-w-[180px] ${isConfirmed ? 'text-green-800' : ''}`} title={e.property_name}>{e.property_name}</span>
                  </div>
                </td>
                <td className={`px-3 py-2 border-r border-slate-100 font-mono text-[10px] ${isConfirmed ? 'text-green-700' : ''}`}>{e.booking_code}</td>
                <td className={`px-3 py-2 border-r border-slate-100 truncate max-w-[140px] ${isConfirmed ? 'text-green-700' : ''}`} title={e.guest_name}>{e.guest_name || '-'}</td>
                <td className="px-3 py-2 border-r border-slate-100 text-center">
                  <Badge className={`text-[10px] ${PLATFORM_COLORS[e.platform] || PLATFORM_COLORS.other}`}>
                    {PLATFORM_LABELS[e.platform] || e.platform}
                  </Badge>
                </td>
                <td className="px-3 py-2 border-r border-slate-100 text-center">
                  <Badge variant="outline" className={`text-[10px] ${e.tariff_type === 'N' ? 'bg-orange-50' : 'bg-green-50'}`}>{e.tariff_type || '-'}</Badge>
                </td>
                <td className="px-3 py-2 border-r border-slate-100 text-center">
                  {isConfirmed ? (
                    <Badge className="text-[9px] bg-green-100 text-green-800 border-green-200">INCASSATO</Badge>
                  ) : isPenalty ? (
                    <Badge className="text-[9px] bg-red-100 text-red-800 border-red-200" data-testid={`penalty-badge-${e.id}`}>PENALE</Badge>
                  ) : (
                    <Badge className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">Da incassare</Badge>
                  )}
                </td>
                <td className="px-3 py-2 border-r border-slate-100 text-center">{fmtDate(e.check_in)}</td>
                <td className="px-3 py-2 border-r border-slate-100 text-center">{fmtDate(e.check_out)}</td>
                <td className={`px-3 py-2 border-r border-slate-100 text-right ${isConfirmed ? 'text-green-700' : ''}`}>{fmtCurrency(e.total_price)}</td>
                <td className={`px-3 py-2 border-r border-slate-100 text-right ${isConfirmed ? 'text-green-700' : ''}`}>{fmtCurrency(e.collected)}</td>
                <td className={`px-3 py-2 border-r border-slate-100 text-right font-bold ${isConfirmed ? 'text-green-700' : isPenalty ? 'text-red-700' : highlightColor}`}>
                  {fmtCurrency(showToCollect ? e.to_collect : e.total_price)}
                </td>
                {onMarkCollected && (
                  <td className="px-3 py-2 text-center">
                    {isConfirmed ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500 inline" data-testid={`confirmed-icon-${e.id}`} />
                    ) : (
                      <Button variant="outline" size="sm"
                        className="h-7 w-7 p-0 border-green-300 hover:bg-green-100 hover:border-green-500"
                        onClick={() => onMarkCollected(e)}
                        disabled={markingId === e.id}
                        data-testid={`mark-collected-btn-${e.id}`}>
                        {markingId === e.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                        ) : (
                          <Check className="h-4 w-4 text-green-600" />
                        )}
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className={`${bgColor} border-t-2 ${borderColor}`}>
            <td colSpan={onMarkCollected ? 9 : 8} className={`px-3 py-2 text-xs font-bold ${textColor}`}>
              TOTALE ({pendingEntries.length} da incassare{confirmedEntries.length > 0 ? `, ${confirmedEntries.length} incassate` : ''})
            </td>
            <td className={`px-3 py-2 text-right text-xs font-bold ${textColor} border-r border-slate-200`}>
              {fmtCurrency(entries.reduce((s, e) => s + (e.collected || 0), 0))}
            </td>
            <td className={`px-3 py-2 text-right text-xs font-bold ${boldColor}`}>
              {fmtCurrency(entries.reduce((s, e) => s + (showToCollect ? (e.to_collect || 0) : (e.total_price || 0)), 0))}
            </td>
            {onMarkCollected && <td />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default function PrenotazioniDaIncassare() {
  const token = localStorage.getItem('hw_token');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState({ date: '', count: 0, total_amount: 0, entries: [], confirmed_count: 0, total_confirmed: 0 });
  const [payments, setPayments] = useState({ count: 0, total_amount: 0, airbnb: { entries: [] }, interhome: { entries: [] } });
  const [upcoming, setUpcoming] = useState({ days: [] });
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [collectRes, paymentsRes, upcomingRes] = await Promise.all([
        fetch(`${API}/api/booking-register/to-collect?target_date=${selectedDate}`, { headers }),
        fetch(`${API}/api/booking-register/payments-arriving?target_date=${selectedDate}`, { headers }),
        fetch(`${API}/api/booking-register/to-collect/upcoming?days=14`, { headers })
      ]);
      if (collectRes.ok) setData(await collectRes.json());
      if (paymentsRes.ok) setPayments(await paymentsRes.json());
      if (upcomingRes.ok) setUpcoming(await upcomingRes.json());
    } catch (e) {
      console.error('Error:', e);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleMarkCollected = async (entry) => {
    try {
      setMarkingId(entry.id);
      const res = await fetch(`${API}/api/booking-register/${entry.id}/mark-collected`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        toast.success(`Incasso confermato per ${entry.guest_name || entry.booking_code}`);
        setData(prev => ({
          ...prev,
          entries: prev.entries.map(e => e.id === entry.id ? { ...e, incassato_confermato: true } : e),
          count: prev.count - 1,
          confirmed_count: (prev.confirmed_count || 0) + 1,
          total_amount: Math.max(0, prev.total_amount - (entry.to_collect || 0)),
          total_confirmed: (prev.total_confirmed || 0) + (entry.to_collect || 0)
        }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore nella conferma');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setMarkingId(null);
    }
  };

  const changeDate = (offset) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];
  const paymentEntries = [...(payments.airbnb?.entries || []), ...(payments.interhome?.entries || [])];

  return (
    <Layout title="Prenotazioni da Incassare">
      <div className="space-y-4">
        {/* Header + date nav */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="page-title">
              <Banknote className="h-6 w-6 text-amber-600" />
              Prenotazioni da Incassare
            </h1>
            <div className="text-sm text-slate-500 mt-0.5 flex items-center">
              <span>{new Date(selectedDate + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
              {isToday && <Badge className="ml-2 bg-amber-100 text-amber-800 text-xs">OGGI</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => changeDate(-1)} data-testid="prev-date-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40 text-sm border rounded-md px-3 py-1.5 border-slate-200"
              data-testid="collection-date-picker" />
            <Button variant="outline" size="sm" onClick={() => changeDate(1)} data-testid="next-date-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
            {!isToday && (
              <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} data-testid="today-btn">
                Oggi
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Da Incassare</p>
                <p className="text-2xl font-bold text-amber-900 mt-0.5" data-testid="collection-count">
                  {data.count}
                  {data.penalty_count > 0 && (
                    <span className="text-sm font-normal text-red-600 ml-2" data-testid="penalty-count-badge">
                      ({data.penalty_count} penali)
                    </span>
                  )}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-amber-300" />
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Totale Da Incassare</p>
                <p className="text-2xl font-bold text-amber-900 mt-0.5" data-testid="collection-total">{fmtCurrency(data.total_amount)}</p>
              </div>
              <Banknote className="h-8 w-8 text-amber-300" />
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wide">Incassati Oggi</p>
                <p className="text-2xl font-bold text-green-900 mt-0.5" data-testid="confirmed-count">
                  {data.confirmed_count || 0}
                  <span className="text-sm font-normal text-green-600 ml-2">({fmtCurrency(data.total_confirmed || 0)})</span>
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-300" />
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">Pagamenti in Arrivo</p>
                <p className="text-2xl font-bold text-emerald-900 mt-0.5" data-testid="payments-count">
                  {payments.count || 0}
                  <span className="text-sm font-normal text-emerald-600 ml-2">
                    ({fmtCurrency(payments.total_amount)})
                  </span>
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-emerald-300" />
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <Card><CardContent className="p-8 text-center text-slate-400"><Loader2 className="h-8 w-8 animate-spin inline mr-2" />Caricamento...</CardContent></Card>
        ) : (
          <>
            {/* Section 1: DA INCASSARE */}
            <Card>
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <Banknote className="h-4 w-4 text-amber-600" />
                <h2 className="text-sm font-bold text-slate-800" data-testid="da-incassare-title">
                  Da Incassare ({data.count})
                  {(data.confirmed_count || 0) > 0 && (
                    <span className="text-green-600 font-normal ml-2">+ {data.confirmed_count} incassate</span>
                  )}
                </h2>
                <span className="text-xs text-slate-400 ml-auto">Booking, Holiday World, Atraveo</span>
              </div>
              <CollectionTable
                entries={data.entries}
                emptyMessage="Nessun incasso previsto per questa data"
                colorScheme="amber"
                showToCollect={true}
                onMarkCollected={handleMarkCollected}
                markingId={markingId}
              />
            </Card>

            {/* Section 2: PAGAMENTI IN ARRIVO */}
            <Card>
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-emerald-600" />
                <h2 className="text-sm font-bold text-slate-800" data-testid="pagamenti-arrivo-title">
                  Pagamenti in Arrivo ({paymentEntries.length})
                </h2>
                <span className="text-xs text-slate-400 ml-auto">Airbnb (check-in + 3gg) / Interhome (check-in - 7gg)</span>
              </div>
              {paymentEntries.length > 0 ? (
                <>
                  {payments.airbnb?.entries?.length > 0 && (
                    <div>
                      <div className="px-4 py-1.5 bg-rose-50/50 border-b border-rose-100">
                        <span className="text-[10px] font-semibold text-rose-700 uppercase">
                          Airbnb ({payments.airbnb.entries.length})
                        </span>
                      </div>
                      <CollectionTable entries={payments.airbnb.entries} emptyMessage="" colorScheme="emerald" showToCollect={false} onMarkCollected={handleMarkCollected} markingId={markingId} />
                    </div>
                  )}
                  {payments.interhome?.entries?.length > 0 && (
                    <div>
                      <div className="px-4 py-1.5 bg-purple-50/50 border-b border-purple-100">
                        <span className="text-[10px] font-semibold text-purple-700 uppercase">
                          Interhome ({payments.interhome.entries.length})
                        </span>
                      </div>
                      <CollectionTable entries={payments.interhome.entries} emptyMessage="" colorScheme="emerald" showToCollect={false} onMarkCollected={handleMarkCollected} markingId={markingId} />
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-400 py-4 text-center">Nessun pagamento in arrivo per questa data</p>
              )}
            </Card>
          </>
        )}

        {/* Upcoming days preview */}
        {upcoming.days && upcoming.days.length > 0 && (
          <Card>
            <CardContent className="p-3">
              <h3 className="text-xs font-bold text-slate-700 mb-2.5 flex items-center gap-2" data-testid="upcoming-title">
                <TrendingUp className="h-3.5 w-3.5" /> Prossimi Incassi (14 giorni)
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {upcoming.days.map(day => {
                  const isSelected = day.date === selectedDate;
                  const isDayToday = day.date === new Date().toISOString().split('T')[0];
                  return (
                    <button key={day.date} onClick={() => setSelectedDate(day.date)}
                      className={`px-2.5 py-1.5 rounded-lg text-[10px] border transition-all ${
                        isSelected ? 'bg-amber-500 text-white border-amber-600 shadow-md' :
                        isDayToday ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200' :
                        'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`upcoming-day-${day.date}`}>
                      <div className="font-bold">{fmtDate(day.date)}</div>
                      <div className={isSelected ? 'text-amber-100' : 'text-slate-500'}>{day.count} pren.</div>
                      <div className={`font-semibold ${isSelected ? 'text-white' : 'text-amber-700'}`}>{fmtCurrency(day.total)}</div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
